import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Card } from "@mui/material";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PieChartRoundedIcon from "@mui/icons-material/PieChartRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../globalState/hooks";
import { LoginRootState } from "../../../types/login";
import CodNewOrderTab from "./CodNewOrderTab";
import CodMyOrdersTab from "./CodMyOrdersTab";
import CodOutstandingTab from "./CodOutstandingTab";
import CodPaymentsTab from "./CodPaymentsTab";
import CodTabBar from "./CodTabBar";
import CodCustomerPicker from "./CodCustomerPicker";
import CodSelectedCustomerCard from "./CodSelectedCustomerCard";
import { CodOrder, CodRepTab, CodReviewStatus, CodServedCustomer } from "../../../types/codSalesRep";
import codService, { deriveCodReviewStatus } from "../../../services/CodService";
import { loadServedCustomer, saveServedCustomer } from "../../../utils/codSession";
import { matchesCodCustomer } from "./codCustomerUtils";
import { codContentCardSx, codKpiGridSx, pageShellSx } from "./codStyles";
import PageHeader from "../../../components/ui/PageHeader";
import ProfileChip from "../../../components/ui/ProfileChip";
import CodStatCard, { CodStatTone } from "../../../components/ui/CodStatCard";

const emptyCounts = (): Record<CodReviewStatus, number> => ({
  pendingReview: 0,
  approved: 0,
  postedToSage: 0,
  rejected: 0,
  partlyPaid: 0,
  paid: 0,
});

const CodSalesRep: React.FC = () => {
  const { t } = useTranslation();
  const userDetails = useAppSelector(
    (state: LoginRootState) => state.authReducer.loginDetials
  );
  const [activeTab, setActiveTab] = useState<CodRepTab>("newOrder");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState<CodServedCustomer | null>(
    () => loadServedCustomer()
  );
  const [orderCounts, setOrderCounts] = useState(emptyCounts);
  const [selectedOutstanding, setSelectedOutstanding] = useState(0);

  const customerOption = useMemo(
    () =>
      selectedCustomer
        ? {
            key: selectedCustomer.id || selectedCustomer.customerCode,
            customerId: selectedCustomer.id,
            customerCode: selectedCustomer.customerCode,
            customerName: selectedCustomer.customerName,
          }
        : null,
    [selectedCustomer]
  );

  const handleSelectCustomer = (customer: CodServedCustomer | null) => {
    setSelectedCustomer(customer);
    saveServedCustomer(customer);
  };

  const refreshOrderCounts = useCallback(async () => {
    try {
      const orders = await codService.listOrders();
      // No customer picked → show the rep's totals across all assigned customers.
      const scoped = customerOption
        ? orders.filter((order) => matchesCodCustomer(order, customerOption))
        : orders;
      const counts = emptyCounts();
      scoped.forEach((order: CodOrder) => {
        const status = deriveCodReviewStatus(order);
        counts[status] += 1;
      });
      setOrderCounts(counts);
    } catch {
      // best-effort
    }
  }, [customerOption]);

  useEffect(() => {
    refreshOrderCounts();
  }, [refreshOrderCounts, refreshKey]);

  const pendingBadge = useMemo(
    () => orderCounts.pendingReview,
    [orderCounts.pendingReview]
  );

  const summaryCards: {
    label: string;
    value: number;
    tone: CodStatTone;
    icon: React.ReactNode;
  }[] = [
    {
      label: t("codSalesRep.pendingApproval"),
      value: orderCounts.pendingReview,
      tone: "warning",
      icon: <HourglassTopRoundedIcon />,
    },
    {
      label: t("codSalesRep.approved"),
      value: orderCounts.approved,
      tone: "info",
      icon: <CheckCircleRoundedIcon />,
    },
    {
      label: t("codSalesRep.partlyPaid"),
      value: orderCounts.partlyPaid,
      tone: "warning",
      icon: <PieChartRoundedIcon />,
    },
    {
      label: t("codSalesRep.postedToSage"),
      value: orderCounts.postedToSage,
      tone: "success",
      icon: <CloudDoneRoundedIcon />,
    },
    {
      label: t("codSalesRep.rejected"),
      value: orderCounts.rejected,
      tone: "danger",
      icon: <CancelRoundedIcon />,
    },
  ];

  const repTabs = useMemo(
    () => [
      {
        value: "newOrder" as const,
        label: t("codSalesRep.newOrder"),
        icon: <AddShoppingCartRoundedIcon sx={{ fontSize: 18 }} />,
      },
      {
        value: "myOrders" as const,
        label: t("codSalesRep.myOrders"),
        count: pendingBadge,
        icon: <AssignmentRoundedIcon sx={{ fontSize: 18 }} />,
      },
      {
        value: "outstanding" as const,
        label: t("codSalesRep.outstanding"),
        icon: <AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} />,
      },
      {
        value: "payments" as const,
        label: t("codSalesRep.payments"),
        icon: <PaymentsRoundedIcon sx={{ fontSize: 18 }} />,
      },
    ],
    [t, pendingBadge]
  );

  const handleTabChange = (value: CodRepTab) => {
    setActiveTab(value);
    if (value === "payments" || value === "myOrders" || value === "outstanding") {
      setRefreshKey((k) => k + 1);
    }
  };

  const handleOrderSubmitted = () => {
    setRefreshKey((k) => k + 1);
    setActiveTab("myOrders");
  };

  const repName = `${userDetails?.firstName ?? ""} ${userDetails?.lastName ?? ""}`.trim();

  return (
    <Box sx={{ ...pageShellSx, maxWidth: 1440, mx: "auto" }}>
      <PageHeader
        title={t("codSalesRep.title")}
        subtitle={t("codSalesRep.subtitle")}
        actions={<ProfileChip name={repName} role={t("codSalesRep.activeRep")} />}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "320px minmax(0, 1fr)" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <CodCustomerPicker
          selected={selectedCustomer}
          onSelect={handleSelectCustomer}
          refreshKey={refreshKey}
          onSelectedOutstandingChange={setSelectedOutstanding}
        />

        <Box sx={{ minWidth: 0 }}>
          <CodSelectedCustomerCard
            customer={selectedCustomer}
            outstanding={selectedOutstanding}
            onClear={() => handleSelectCustomer(null)}
          />

          <Box sx={{ containerType: "inline-size" }}>
            <Box
              sx={{
                ...codKpiGridSx,
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                "@container (min-width: 560px)": { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
                "@container (min-width: 860px)": { gridTemplateColumns: "repeat(5, minmax(0, 1fr))" },
              }}
            >
              {summaryCards.map((card) => (
                <CodStatCard
                  key={card.label}
                  label={card.label}
                  value={card.value}
                  tone={card.tone}
                  icon={card.icon}
                />
              ))}
            </Box>
          </Box>

      <CodTabBar value={activeTab} tabs={repTabs} onChange={handleTabChange} />

      <Card sx={codContentCardSx}>
        {activeTab === "newOrder" && (
          <CodNewOrderTab
            customer={selectedCustomer}
            onOrderSubmitted={handleOrderSubmitted}
            onCustomerCleared={() => handleSelectCustomer(null)}
          />
        )}
        {activeTab === "myOrders" && (
          <CodMyOrdersTab
            refreshKey={refreshKey}
            customer={customerOption}
            onCountsChange={customerOption ? setOrderCounts : undefined}
            onPosted={() => setRefreshKey((k) => k + 1)}
          />
        )}
        {activeTab === "outstanding" && (
          <CodOutstandingTab refreshKey={refreshKey} customer={customerOption} />
        )}
        {activeTab === "payments" && (
          <CodPaymentsTab
            refreshKey={refreshKey}
            customer={customerOption}
            onPosted={() => setRefreshKey((k) => k + 1)}
          />
        )}
      </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default CodSalesRep;
